/**
 * ModelConfigRenderer - Image generation model configuration
 *
 * Used by Generate, Edit, and Fuse nodes for model selection and parameters.
 * Includes: model selection, size, aspect ratio, quality, and web search toggle.
 * Note: This renderer does not support field mapping as model config is not mappable.
 */

import React, { useEffect } from 'react';
import { ModelSelector } from '@/features/playground/components/ModelSelector';
import { useImageModels, useDynamicImageConfig } from '@/features/playground/hooks/usePlayground';
import { Select } from '@/components/ui/Select';
import type { BaseCustomRendererProps } from './types';

export const ModelConfigRenderer: React.FC<BaseCustomRendererProps> = ({
  nodeData,
  isDisabled = false,
  nodeType,
  onBatchUpdate,
}) => {
  // Determine operation mode based on node type
  const operationMode = nodeType === 'edit' ? 'edit' : nodeType === 'fuse' ? 'fuse' : 'generate';

  // Fetch models
  const { data: modelsData, isLoading: modelsLoading } = useImageModels();
  const models = modelsData?.models || [];

  // Dynamic config for model selection
  const {
    config,
    currentModel,
    setModel,
    setSize,
    setAspectRatio,
    setQuality,
    setWebSearch,
  } = useDynamicImageConfig(models, operationMode);

  // Sync node model with dynamic config on mount
  useEffect(() => {
    if (nodeData.model && nodeData.model !== 'auto') {
      const modelExists = models.find((m) => m.model_id === nodeData.model);
      if (modelExists) {
        setModel(nodeData.model as string);
      }
    }
  }, [nodeData.model, models, setModel]);

  // Handler for model change
  const handleModelChange = (modelId: string) => {
    setModel(modelId);
    if (onBatchUpdate) {
      onBatchUpdate({ model: modelId });
    }
  };

  // Handler for size change
  const handleSizeChange = (size: string) => {
    setSize(size);
    if (onBatchUpdate) {
      onBatchUpdate({ size });
    }
  };

  // Handler for aspect ratio change
  const handleAspectRatioChange = (ratio: string) => {
    setAspectRatio(ratio);
    if (onBatchUpdate) {
      onBatchUpdate({ aspect_ratio: ratio });
    }
  };

  // Handler for quality change
  const handleQualityChange = (quality: string) => {
    setQuality(quality || null);
    if (onBatchUpdate) {
      onBatchUpdate({ quality: quality || undefined });
    }
  };

  // Handler for web search toggle
  const handleWebSearchChange = (enabled: boolean) => {
    setWebSearch(enabled);
    if (onBatchUpdate) {
      onBatchUpdate({ web_search: enabled });
    }
  };

  return (
    <div className="space-y-4">
      {/* Model Selector */}
      <ModelSelector
        models={models}
        selectedModel={config.model}
        onModelChange={handleModelChange}
        operationMode={operationMode}
        isLoading={modelsLoading}
      />

      {/* Size and Aspect Ratio */}
      {currentModel && (
        <div className="grid grid-cols-2 gap-3">
          {/* Size Selection */}
          {currentModel.sizes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                分辨率
              </label>
              <div className="flex flex-wrap rounded-lg overflow-hidden border border-gray-200">
                {currentModel.sizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => handleSizeChange(size)}
                    disabled={isDisabled}
                    className={`flex-1 min-w-[50px] px-2 py-1.5 text-xs font-medium transition-colors ${
                      config.size === size
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Aspect Ratio Selection */}
          {currentModel.aspect_ratios.length > 0 && (
            <Select
              label="宽高比"
              value={config.aspect_ratio}
              onChange={(e) => handleAspectRatioChange(e.target.value)}
              disabled={isDisabled}
              options={currentModel.aspect_ratios.map((ratio) => ({
                value: ratio,
                label: ratio,
              }))}
            />
          )}
        </div>
      )}

      {/* Quality (if supported) */}
      {currentModel && currentModel.quality_options.length > 0 && (
        <Select
          label="质量"
          value={config.quality || ''}
          onChange={(e) => handleQualityChange(e.target.value)}
          disabled={isDisabled}
          options={[
            { value: '', label: '默认' },
            ...currentModel.quality_options.map((q) => ({
              value: q,
              label: q,
            })),
          ]}
        />
      )}

      {/* Web Search Toggle */}
      {currentModel?.supports_web_search && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <input
            type="checkbox"
            id="model_config_web_search"
            checked={config.web_search}
            onChange={(e) => handleWebSearchChange(e.target.checked)}
            disabled={isDisabled}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <div>
            <label
              htmlFor="model_config_web_search"
              className="text-sm font-medium text-gray-700 cursor-pointer"
            >
              启用网络搜索
            </label>
            <p className="text-xs text-gray-500">
              允许模型搜索网络获取参考素材
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelConfigRenderer;
